import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import moment from 'moment-timezone';
import Pagination from 'rc-pagination';

import { ExportWorksheetIcon } from 'icons';

import { MaintenanceValidationHistItem } from '../../types';
import { CardContainer } from '../../styles';
import {
  exportMaintenanceValidationHistory, getMaintenanceValidationHistory, MaintenanceValidationHistoryResult,
} from '../../helpers/api';
import {
  HistoryCardWrap, HistoryHeader, HistoryTitle, EmptyState,
  DataTable, Th, TdNumber, Td, TdNote, ExportButton, PaginationWrap,
} from './styles';
import { HistoryResult } from './HistoryResult';

export const PAGE_SIZE = 4;

interface HistoryTableProps {
  isLoading: boolean;
  timezoneArea?: string | null;
  devId: string;
  machineId: number | null | undefined;
  refreshToken: number;
  initialHistory?: MaintenanceValidationHistoryResult | null;
}

export function HistoryTable({
  isLoading, timezoneArea, devId, machineId, refreshToken, initialHistory,
}: Readonly<HistoryTableProps>): JSX.Element {
  const { t } = useTranslation();
  const [isExporting, setIsExporting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageHistory, setPageHistory] = useState<MaintenanceValidationHistItem[]>(initialHistory?.list ?? []);
  const [pageTotal, setPageTotal] = useState(initialHistory?.total ?? 0);
  const [isLoadingPage, setIsLoadingPage] = useState(false);
  const hasConsumedInitialHistory = useRef(false);

  async function fetchPage(page: number) {
    if (!machineId) return;
    setIsLoadingPage(true);
    try {
      const resp = await getMaintenanceValidationHistory({
        device_code: devId,
        machine_id: machineId,
        skip: (page - 1) * PAGE_SIZE,
        limit: PAGE_SIZE,
      });
      if (resp) {
        setPageHistory(resp.list ?? []);
        setPageTotal(resp.total ?? 0);
        setCurrentPage(page);
      }
    } finally {
      setIsLoadingPage(false);
    }
  }

  useEffect(() => {
    if (isLoading) return;
    if (!hasConsumedInitialHistory.current && initialHistory) {
      hasConsumedInitialHistory.current = true;
      setPageHistory(initialHistory.list ?? []);
      setPageTotal(initialHistory.total ?? 0);
      return;
    }
    hasConsumedInitialHistory.current = true;
    fetchPage(currentPage);
  }, [machineId, refreshToken, isLoading]);

  function handlePageChange(page: number) {
    fetchPage(page);
  }

  function formatDate(isoString: string) {
    return timezoneArea
      ? moment.tz(isoString, timezoneArea).format('DD/MM/YYYY, HH:mm')
      : moment(isoString).format('DD/MM/YYYY, HH:mm');
  }

  function getDateLabel(item: MaintenanceValidationHistItem) {
    if (item.completed_at) return formatDate(item.completed_at);
    if (item.active) return '--';
    return formatDate(item.start_time);
  }

  async function handleExport() {
    if (!machineId) return;
    setIsExporting(true);
    try {
      const resp = await exportMaintenanceValidationHistory({ device_code: devId, machine_id: machineId });
      const filename = (resp.headers.filename as string | undefined) ?? `${t('nomeArquivoHistoricoValidacaoManutencao')}.xlsx`;
      const url = window.URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      toast.success(t('sucessoExportacao'));
    } catch (err) {
      console.error(err);
      toast.error(t('naoFoiPossivelExportar'));
    } finally {
      setIsExporting(false);
    }
  }

  if (isLoading) return <></>;

  const firstRowNumber = pageTotal - (currentPage - 1) * PAGE_SIZE;

  return (
    <HistoryCardWrap>
      <CardContainer>
        <HistoryHeader>
          <HistoryTitle>{t('historicoManutencoesVistoriadas')}</HistoryTitle>
          {pageHistory.length > 0 && (
            <ExportButton type="button" onClick={handleExport} disabled={isExporting || !machineId}>
              <ExportWorksheetIcon />
              {t('exportarPlanilha')}
            </ExportButton>
          )}
        </HistoryHeader>

        {pageHistory.length === 0 ? (
          <EmptyState>{t('nenhumaValidacaoRealizada')}</EmptyState>
        ) : (
          <>
            <DataTable>
              <thead>
                <tr>
                  <Th>#</Th>
                  <Th>{t('dataDaVistoria')}</Th>
                  <Th>{t('resultadoDaVistoria')}</Th>
                  <Th>{t('saudePreValidacaoColuna')}</Th>
                </tr>
              </thead>
              <tbody>
                {pageHistory.map((item, idx) => (
                  <tr key={item.id}>
                    <TdNumber>{firstRowNumber - idx}</TdNumber>
                    <Td>
                      <div>{getDateLabel(item)}</div>
                      <TdNote>{t('tecnicoVistoria', { name: item.user_email })}</TdNote>
                    </Td>
                    <Td>
                      <HistoryResult item={item} timezoneArea={timezoneArea} />
                    </Td>
                    <Td>{item.health_desc_pre_analysis ?? '-'}</Td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
            {pageTotal > PAGE_SIZE && (
              <PaginationWrap>
                <Pagination
                  className="ant-pagination"
                  current={currentPage}
                  total={pageTotal}
                  pageSize={PAGE_SIZE}
                  onChange={handlePageChange}
                  disabled={isLoadingPage}
                />
              </PaginationWrap>
            )}
          </>
        )}
      </CardContainer>
    </HistoryCardWrap>
  );
}
